import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { PLAN_LABEL, PLAN_PERKS, planWith, SELLER_PLANS } from "../../shared/market";
import type { SellerPerformance, StatsListing } from "../../shared/marketStats";
import { sellerPath } from "../../shared/sellerPaths";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi, type MySeller, type QuickReply } from "./api";
import { useArs } from "./components";

const listingName = (l: StatsListing) => [l.name, l.setName, l.cardNumber && `#${l.cardNumber}`].filter(Boolean).join(" · ");

/** Estadísticas › Resumen (VMAX): where the visits to the shop's listings came from, in the period. */
export function Sources({ sources }: { sources: SellerPerformance["sources"] }) {
  const { t } = useI18n();
  return (
    <section aria-labelledby="perf-src">
      <h3 id="perf-src">{t("perf.sources")}</h3>
      <p className="muted small">{t("perf.sourcesHint")}</p>
      {sources.length === 0 ? (
        <p className="muted">{t("perf.sourcesEmpty")}</p>
      ) : (
        <ul className="source-bars">
          {sources.map((s) => (
            <li key={s.source}>
              <span>{t(`perf.src.${s.source}` as MessageKey)}</span>
              <span className="source-bar" aria-hidden="true">
                <span style={{ width: `${Math.max(2, s.pct)}%` }} />
              </span>
              <strong>{s.pct}%</strong> <span className="muted small">({s.views})</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** "Bajar precio": Publicaciones, already filtered to that card. */
const lowerPriceTo = (l: StatsListing) => `${sellerPath("publicaciones")}?q=${encodeURIComponent(l.name)}`;

/**
 * Estadísticas › Cartas (VMAX), first: what nobody looks at and what gets saved but not bought, each with
 * what to do about it (pause, feature, lower the price).
 */
export function NeedsPush({ d, canFeature = true }: { d: SellerPerformance; /** Featured places left this month (and not a new shop). */ canFeature?: boolean }) {
  const { t } = useI18n();
  const ars = useArs();
  const [done, setDone] = useState<Record<number, "paused" | "featured">>({});
  const pause = async (id: number) => {
    await marketApi.updateListing(id, { status: "paused" });
    setDone((x) => ({ ...x, [id]: "paused" }));
  };
  const feature = async (id: number) => {
    await marketApi.featureListing(id);
    setDone((x) => ({ ...x, [id]: "featured" }));
  };
  return (
    <section aria-labelledby="perf-push" className="stack-sm">
      <h3 id="perf-push">{t("perf.needsPush")}</h3>
      <div>
        <h4 id="perf-nov">{t("perf.noViews")}</h4>
        <p className="muted small">{t("perf.noViewsHint")}</p>
        {d.noViews.length === 0 ? (
          <p className="muted">{t("perf.none")}</p>
        ) : (
          <ul className="stats-list push-list" aria-labelledby="perf-nov">
            {d.noViews.map((l) => (
              <li key={l.id}>
                <span>
                  <Link to={`/mercado/${l.id}`}>{listingName(l)}</Link> <span className="muted small">· {ars(l.priceArs)}</span>
                </span>
                <span className="btn-row">
                  <Link to={lowerPriceTo(l)} className="btn btn-outline btn-sm">
                    {t("perf.lowerPrice")}
                  </Link>
                  {done[l.id] === "paused" ? (
                    <span className="muted small">{t("perf.paused")}</span>
                  ) : (
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => void pause(l.id)}>
                      {t("perf.pause")}
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <h4 id="perf-saved">{t("perf.savedNotSold")}</h4>
        <p className="muted small">{t("perf.savedNotSoldHint")}</p>
        {d.savedNotSold.length === 0 ? (
          <p className="muted">{t("perf.none")}</p>
        ) : (
          <ul className="stats-list push-list" aria-labelledby="perf-saved">
            {d.savedNotSold.map((l) => (
              <li key={l.id}>
                <span>
                  <Link to={`/mercado/${l.id}`}>{listingName(l)}</Link> <span className="muted small">· {ars(l.priceArs)} · {t("perf.favorites", { count: l.favorites })}</span>
                </span>
                <span className="btn-row">
                  {done[l.id] === "featured" || !l.featurable ? (
                    <span className="muted small">{t("perf.featured")}</span>
                  ) : canFeature ? (
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => void feature(l.id).catch(() => setDone((x) => ({ ...x })))}>
                      {t("perf.feature")}
                    </button>
                  ) : null}
                  <Link to={lowerPriceTo(l)} className="btn btn-outline btn-sm">
                    {t("perf.lowerPrice")}
                  </Link>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Mi tienda › Ajustes: the shop's short link and its QR (every plan). */
export function ShopLinkCard({ handle }: { handle: string }) {
  const { t } = useI18n();
  // The address to share is /@handle; the QR goes through /t/, so its visits count as "QR" in Rendimiento.
  const url = `${window.location.origin}/@${handle}`;
  const qrUrl = `${window.location.origin}/t/${handle}`;
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let alive = true;
    import("qrcode")
      .then((QR) => QR.toDataURL(qrUrl, { margin: 1, width: 240 }))
      .then((data) => alive && setQr(data))
      .catch(() => alive && setQr(null));
    return () => {
      alive = false;
    };
  }, [qrUrl]);
  return (
    <section className="plan-card shop-link" aria-labelledby="link-title">
      <h2 id="link-title" className="small-title">
        {t("link.title")}
      </h2>
      <p className="muted small">{t("link.hint")}</p>
      <p>
        <code>{url.replace(/^https?:\/\//, "")}</code>{" "}
        <button
          type="button"
          className="btn btn-link btn-sm"
          onClick={() => void navigator.clipboard?.writeText(url).then(() => setCopied(true))}
        >
          {copied ? t("mkt.copied") : t("link.copy")}
        </button>
      </p>
      {qr && (
        <p>
          <img src={qr} alt={`QR ${url}`} width={160} height={160} />{" "}
          <a href={qr} download={`pokefans-${handle}-qr.png`} className="btn btn-outline btn-sm">
            {t("link.download")}
          </a>
        </p>
      )}
    </section>
  );
}

/** Mi tienda › Ajustes: the shop's saved chat answers (EX 5, VMAX 20), or which plan has them. */
export function QuickRepliesCard() {
  const { t } = useI18n();
  const data = useAsync((signal) => marketApi.quickReplies(signal), []);
  const [replies, setReplies] = useState<QuickReply[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  if (!data.data) return null;
  const { max } = data.data;
  const list = replies ?? data.data.replies;
  if (max === 0) {
    return (
      <section className="plan-card" aria-label={t("qr.title")}>
        <p>{t("qr.teaser", { plan: PLAN_LABEL[SELLER_PLANS.find((p) => PLAN_PERKS[p].quickReplies > 0)!] })}</p>
      </section>
    );
  }
  const add = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setError(null);
    try {
      setReplies((await marketApi.addQuickReply(String(f.get("title") ?? ""), String(f.get("body") ?? ""))).replies);
      setFormKey((n) => n + 1);
    } catch (err) {
      setError(err instanceof ApiError && err.code === "quick_reply_limit" ? t("qr.limit") : t("error.generic"));
    }
  };
  return (
    <section className="plan-card stack-sm" aria-labelledby="qr-title">
      <h2 id="qr-title" className="small-title">
        {t("qr.title")} <span className="muted small">{t("qr.count", { count: list.length, max })}</span>
      </h2>
      <p className="muted small">{t("qr.hint")}</p>
      {list.length > 0 && (
        <ul className="stats-list">
          {list.map((r) => (
            <li key={r.id}>
              <strong>{r.title}</strong> <span className="muted small">{r.body.length > 80 ? `${r.body.slice(0, 80)}…` : r.body}</span>{" "}
              <button type="button" className="btn btn-link btn-sm" onClick={() => void marketApi.deleteQuickReply(r.id).then((x) => setReplies(x.replies))}>
                {t("qr.remove")}
              </button>
            </li>
          ))}
        </ul>
      )}
      {list.length < max && (
        <form key={formKey} className="stack-sm" onSubmit={(e) => void add(e)}>
          <div className="field">
            <label htmlFor="qr-title-in">{t("qr.titleLabel")}</label>
            <input id="qr-title-in" name="title" required maxLength={40} />
          </div>
          <div className="field">
            <label htmlFor="qr-body-in">{t("qr.bodyLabel")}</label>
            <textarea id="qr-body-in" name="body" rows={3} required maxLength={1000} />
          </div>
          <div>
            <button type="submit" className="btn btn-outline btn-sm">
              {t("qr.add")}
            </button>
          </div>
        </form>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
    </section>
  );
}

/** Mi tienda › Ajustes: the emails a VMAX shop gets about itself, both off until it turns them on. */
export function ReportsCard({ seller, allowed, onSaved }: { seller: MySeller; allowed: boolean; onSaved: (s: MySeller) => void }) {
  const { t } = useI18n();
  const [note, setNote] = useState<string | null>(null);
  if (!allowed) {
    return (
      <section className="plan-card" aria-label={t("rep.title")}>
        <p>{t("rep.teaser", { plan: PLAN_LABEL[planWith("performance")] })}</p>
      </section>
    );
  }
  const current = seller.reports ?? { weekly: false, priceAlerts: false };
  const save = async (patch: Partial<{ weekly: boolean; priceAlerts: boolean }>) => {
    setNote(null);
    try {
      onSaved(await marketApi.setReports({ ...current, ...patch }));
      setNote(t("rep.saved"));
    } catch {
      setNote(t("error.generic"));
    }
  };
  return (
    <section className="plan-card stack-sm" aria-labelledby="rep-title">
      <h2 id="rep-title" className="small-title">
        {t("rep.title")}
      </h2>
      <label className="check">
        <input type="checkbox" checked={current.weekly} onChange={(e) => void save({ weekly: e.target.checked })} /> {t("rep.weekly")}
      </label>
      <label className="check">
        <input type="checkbox" checked={current.priceAlerts} onChange={(e) => void save({ priceAlerts: e.target.checked })} /> {t("rep.prices")}
      </label>
      {note && <p className="small" role="status">{note}</p>}
    </section>
  );
}

/** In Ventas (VMAX): the last 12 months by month and payment method, for the accountant. */
export function AccountingExport() {
  const { t } = useI18n();
  return (
    <section className="plan-card orders-export" aria-labelledby="acc-title">
      <h2 id="acc-title" className="small-title">
        {t("rep.accounting")}
      </h2>
      <p className="muted small">{t("rep.accountingHint")}</p>
      <a className="btn btn-outline btn-sm" href={marketApi.accountingCsvUrl()} download>
        {t("rep.accountingDownload")}
      </a>
    </section>
  );
}
